import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { AddWriteoffApprovalLevelDialog } from '../dialogs/AddWriteoffApprovalLevelDialog';
import { AddLevelButton } from '../commands/AddLevelButton';
import { SaveWriteoffConfigButton } from '../commands/SaveWriteoffConfigButton';
import {
  useConfigureWriteoffApprovalMutation,
} from '../../../../services/ConfigurationService';
import { WriteoffApprovalLevelType } from '../../../../services/ConfigurationService/types/writeoffApprovalTypes';

interface LevelFormData {
  level: number;
  isEnabled: boolean;
  primaryApprover: string;
  backupApprover: string;
  minAmount: number;
  maxAmount: number;
}

interface WriteoffApprovalLevelsDisplayProps {
  centerId: string;
  initialConfig?: {
    approvalLevels: WriteoffApprovalLevelType[];
  };
}

export const WriteoffApprovalLevelsDisplay: React.FC<WriteoffApprovalLevelsDisplayProps> = ({
  centerId,
  initialConfig,
}) => {
  const [levels, setLevels] = useState<LevelFormData[]>(
    initialConfig?.approvalLevels?.map(l => ({
      level: l.level,
      isEnabled: l.isEnabled,
      primaryApprover: l.primaryApprover,
      backupApprover: l.backupApprover,
      minAmount: l.minAmount,
      maxAmount: l.maxAmount,
    })) || []
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState<LevelFormData | null>(null);

  const [configureWriteoffApproval, { isLoading: isSaving, error: saveError }] = useConfigureWriteoffApprovalMutation();

  const handleAddLevel = () => {
    setEditingLevel(null);
    setDialogOpen(true);
  };

  const handleEditLevel = (level: LevelFormData) => {
    setEditingLevel(level);
    setDialogOpen(true);
  };

  const handleDeleteLevel = (levelToDelete: LevelFormData) => {
    setLevels(levels.filter(l => l.level !== levelToDelete.level));
  };

  const handleSaveLevel = (levelData: LevelFormData) => {
    if (editingLevel) {
      // Update existing level
      setLevels(levels.map(l => l.level === editingLevel.level ? levelData : l));
    } else {
      // Add new level
      setLevels([...levels, levelData]);
    }
    setDialogOpen(false);
  };

  const handleSaveConfiguration = async () => {
    try {
      await configureWriteoffApproval({
        centerId,
        writeoffApprovalConfig: {
          approvalLevels: levels,
        },
      }).unwrap();
      
      // Show success message or handle success
      console.log('Writeoff configuration saved successfully');
    } catch (error) {
      console.error('Error saving writeoff configuration:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ color: '#1e293b', fontWeight: 600 }}>
            Writeoff Approval Configuration
          </Typography>
          <Stack direction="row" spacing={2}>
            <AddLevelButton onClick={handleAddLevel} />
            <SaveWriteoffConfigButton
              onClick={handleSaveConfiguration}
              isLoading={isSaving}
              disabled={levels.length === 0}
            />
          </Stack>
        </Box>

        {/* Error Alert */}
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error saving configuration. Please try again.
          </Alert>
        )}

        {/* Approval Levels */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Approval Levels ({levels.length})
          </Typography>
          
          {levels.length === 0 ? (
            <Card sx={{ textAlign: 'center', py: 4 }}>
              <CardContent>
                <Typography variant="body1" color="text.secondary">
                  No writeoff approval levels configured. Click "Add Level" to get started.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Stack spacing={2}>
              {levels
                .sort((a, b) => a.level - b.level)
                .map((level) => (
                  <Card key={level.level} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Stack spacing={2} sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h6" sx={{ color: '#1e293b' }}>
                              Level {level.level}
                            </Typography>
                            <Chip
                              label={level.isEnabled ? 'Enabled' : 'Disabled'}
                              color={level.isEnabled ? 'success' : 'default'}
                              size="small"
                            />
                          </Box>
                          
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            <strong>Amount Range:</strong> {formatCurrency(level.minAmount)} - {formatCurrency(level.maxAmount)}
                          </Typography>
                          
                          <Stack direction="row" spacing={4}>
                            <Box>
                              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                                Primary Approver
                              </Typography>
                              <Typography variant="body2">{level.primaryApprover}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                                Backup Approver
                              </Typography>
                              <Typography variant="body2">{level.backupApprover}</Typography>
                            </Box>
                          </Stack>
                        </Stack>
                        
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditLevel(level)}
                            sx={{ color: '#3b82f6' }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteLevel(level)}
                            sx={{ color: '#ef4444' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
            </Stack>
          )}
        </Box>
      </Stack>

      {/* Add/Edit Level Dialog */}
      <AddWriteoffApprovalLevelDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveLevel}
        editingLevel={editingLevel}
        nextLevelNumber={levels.length + 1}
      />
    </Box>
  );
};
